/* A single guess row. Walks the original sentence char-by-char so spacing
   and punctuation render in their canonical positions; letter slots come
   from the supplied buffer. */

import { useEffect, useRef, useState } from "preact/hooks";
import {
  buildRevealSlots,
  changedLetterIndices,
  prefersReducedMotion,
  revealDelayFor,
} from "../effects/reveal";
import type { PuzzleAnalysis } from "../game/puzzle";
import type { Mark } from "../game/scoring";
import { Cell } from "./Cell";

const SHAKE_MS = 320;

type Props = {
  analysis: PuzzleAnalysis;
  /** Length = analysis.letterCount. null cells render blank. */
  letters: readonly (string | null)[];
  /** Same length as `letters`, optional. */
  marks?: readonly Mark[] | undefined;
  /** Letter index in [0, letterCount) to highlight as cursor pair, or null. */
  cursorLetterIndex?: number | null;
  /** Render as a reveal-paid row: empty, struck-through, dimmed. The
      kind picks the strike color: "hint" → orange, "spaces" → blue. */
  paid?: "hint" | "spaces" | undefined;
  /** When true, this is the just-submitted row — drive the outside-in
      flip-reveal. Pairs whose letters changed since the previous guess
      animate with a horizontal-axis flip; pairs that carried forward
      unchanged (typically green-locked positions) snap to their mark
      immediately and don't consume a stagger slot. Reduced-motion users
      skip the flip entirely. */
  revealing?: boolean;
  /** Previous submitted guess, used to detect which positions the player
      changed — only those flip. null on the very first guess (every
      position is treated as changed). */
  previousLetters?: readonly (string | null)[] | null;
  /** When true, mark this row's div with `data-current-row` so
      `<BoardScroll />` can scrollIntoView it as the player advances
      down the board. */
  currentRow?: boolean;
  /** Monotonic counter from state.shakeNonce. When it changes, the row
      runs one row-shake animation cycle. Only the active row receives
      this from <Board />; other rows leave it undefined. */
  shakeNonce?: number;
};

export function Row({
  analysis,
  letters,
  marks,
  cursorLetterIndex = null,
  paid,
  revealing = false,
  previousLetters = null,
  currentRow = false,
  shakeNonce,
}: Props) {
  const [shaking, setShaking] = useState(false);
  const lastNonce = useRef(shakeNonce);
  useEffect(() => {
    if (shakeNonce == null) return;
    if (shakeNonce === lastNonce.current) return;
    lastNonce.current = shakeNonce;
    setShaking(true);
    const t = window.setTimeout(() => setShaking(false), SHAKE_MS);
    return () => window.clearTimeout(t);
  }, [shakeNonce]);

  const { sentence, letterIndices, pivotLetterIndices, letterCount } = analysis;
  const mirrorOfCursor =
    cursorLetterIndex == null ? null : letterCount - 1 - cursorLetterIndex;
  const pivotSet = new Set(pivotLetterIndices);
  const animateReveal = revealing && !prefersReducedMotion();
  const slots = animateReveal
    ? buildRevealSlots(
        changedLetterIndices(letters, previousLetters),
        letterCount,
      )
    : null;

  // Build a quick lookup: char-index in sentence → letter-index, or undefined.
  const charToLetter = new Map<number, number>();
  for (let li = 0; li < letterIndices.length; li++) {
    charToLetter.set(letterIndices[li]!, li);
  }

  const out: preact.JSX.Element[] = [];
  for (let i = 0; i < sentence.length; i++) {
    const ch = sentence[i]!;
    const li = charToLetter.get(i);
    if (li !== undefined) {
      const isActive = li === cursorLetterIndex || li === mirrorOfCursor;
      const isPivot = pivotSet.has(li);
      // Pivot tick shows on every unsubmitted row (no marks). Cursor highlight
      // visually wins when both apply at the same cell.
      const showPivot = isPivot && !marks;
      const delay = slots
        ? revealDelayFor(li, letterCount, slots)
        : null;
      out.push(
        <Cell
          key={`c-${i}`}
          letter={letters[li] ?? null}
          mark={marks?.[li]}
          active={isActive}
          pivot={showPivot}
          revealDelayMs={delay ?? undefined}
        />,
      );
    } else if (ch === " ") {
      out.push(<span key={`s-${i}`} class="sep sep--space" />);
    } else {
      out.push(
        <span key={`p-${i}`} class="sep sep--punct">
          {ch}
        </span>,
      );
    }
  }

  // The row containing the cursor lifts above sibling rows so the
  // cell--active outline (which projects 2px into the gutter) isn't
  // painted over by the next row's cells when --cell-gap is small —
  // each row is its own stacking context (perspective: 800px), so a
  // z-index on the active cell alone can't escape its row.
  const cls = [
    "row",
    paid ? `row--paid row--paid-${paid}` : "",
    cursorLetterIndex != null ? "row--active" : "",
    shaking ? "row--shake" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div
      class={cls}
      aria-label={
        paid === "hint"
          ? "Hint-paid row"
          : paid === "spaces"
            ? "Spaces-paid row"
            : undefined
      }
      data-current-row={currentRow ? "true" : undefined}
    >
      {out}
    </div>
  );
}
